import useToastContext from "hooks/useToastContext";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost, apiPut } from "utils/apiFetch";
import apiPath from "utils/apiPath";

function AddTrainingSession() {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const [form, setForm] = useState({
    date: "",
    startTime: "",
    endTime: "",
    location: "",
    seats: "",
  });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const notification = useToastContext();

  useEffect(() => {
    if (!id) return;

    async function fetchTrainingSession() {
      try {
        const response = await apiGet(`${apiPath.trainingSessions}/${id}`);
        const session = response?.data?.results;
        setForm({
          date: session?.date?.slice(0, 10) || "",
          startTime: session?.startTime || "",
          endTime: session?.endTime || "",
          location: session?.location || "",
          seats: session?.seats || "",
        });
      } catch (error) {
        notification.error(
          error?.response?.data?.message ||
            "Unable to load the training session.",
        );
      }
    }

    fetchTrainingSession();
  }, [id, notification]);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (form.endTime <= form.startTime) {
      notification.error("End time must be after start time.");
      return;
    }

    setSaving(true);
    try {
      const payload = { ...form, seats: Number(form.seats) };
      const response = isEditing
        ? await apiPut(`${apiPath.trainingSessions}/${id}`, payload)
        : await apiPost(apiPath.trainingSessions, payload);
      if (!response?.data?.success) return;

      notification.success(
        `Training session ${isEditing ? "updated" : "created"} successfully.`,
      );
      navigate("/training-sessions");
    } catch (error) {
      notification.error(
        error?.response?.data?.message ||
          `Unable to ${isEditing ? "update" : "create"} training session.`,
      );
    } finally {
      setSaving(false);
    }
  }

  const fieldClass =
    "bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-0 focus:border-black block w-full p-2.5";

  return (
    <div className="bg-white px-3 py-4">
      <h1 className="text-2xl font-bold">
        {isEditing ? "Edit" : "Create"} Training Session
      </h1>
      <hr className="my-4" />

      <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2 text-sm font-medium">
            <span>
              Date <sup className="text-red-500">*</sup>
            </span>
            <input
              className={fieldClass}
              type="date"
              name="date"
              value={form.date}
              onChange={handleChange}
              placeholder="Select date"
              required
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            <span>
              Location <sup className="text-red-500">*</sup>
            </span>
            <input
              className={fieldClass}
              type="text"
              name="location"
              value={form.location}
              onChange={handleChange}
              placeholder="Enter location"
              required
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            <span>
              Start Time <sup className="text-red-500">*</sup>
            </span>
            <input
              className={fieldClass}
              type="time"
              name="startTime"
              value={form.startTime}
              onChange={handleChange}
              placeholder="Select start time"
              required
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            <span>
              End Time <sup className="text-red-500">*</sup>
            </span>
            <input
              className={fieldClass}
              type="time"
              name="endTime"
              value={form.endTime}
              onChange={handleChange}
              placeholder="Select end time"
              required
            />
          </label>
          <label className="flex flex-col gap-2 text-sm font-medium">
            <span>
              Seats <sup className="text-red-500">*</sup>
            </span>
            <input
              className={fieldClass}
              type="number"
              name="seats"
              min="1"
              step="1"
              value={form.seats}
              onChange={handleChange}
              placeholder="Enter number of seats"
              required
            />
          </label>
        </div>

        <hr className="my-4" />
        <div className="flex justify-center gap-4">
          <button
            type="button"
            className="rounded bg-gray-200 px-8 py-2.5 text-sm hover:bg-gray-300 transition-colors duration-150"
            onClick={() => navigate("/training-sessions")}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded bg-[#8FC150] px-8 py-2.5 text-sm text-white hover:bg-[#7eae43] transition-colors duration-150 disabled:bg-gray-500 disabled:hover:bg-gray-500"
          >
            {saving
              ? `${isEditing ? "Updating" : "Creating"}...`
              : `${isEditing ? "Update" : "Create"} Training Session`}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AddTrainingSession;
